import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Bell,
  Bookmark,
  Calendar,
  CreditCard,
  Heart,
  LayoutDashboard,
  MessageSquare,
  Search,
  Settings,
  Shield,
  Users,
  Wallet,
} from "lucide-react";

import {
  MenuBreadcrumb,
  MenuItemList,
  SectionLabel,
  type MenuItem,
} from "@/components/account/menu-shared";
import { ProtectionLockIcon } from "@/components/account/protection-lock-icon";
import { UpgradeIncentiveCard } from "@/components/account/upgrade-incentive-card";
import { WorkspaceFooterSections } from "@/components/account/workspace-footer-sections";
import { Skeleton } from "@/components/ui/skeleton";
import { useDesktopRedirect } from "@/lib/hooks/use-desktop-redirect";
import { useReferralProgram } from "@/lib/hooks/use-referral-program";

interface SidebarData {
  notificationCount?: number;
  /** Unread direct messages — drives the Messages badge (same field the desktop side-nav reads). */
  unreadMessageCount?: number;
  vaultProtection?: string | null;
  subscriptionPlan?: string | null;
  countryReferralEnabled?: boolean;
  profile?: {
    appointmentCount?: number;
    favoriteCount?: number;
    documentCount?: number;
    waitlistCount?: number;
    savedSearchCount?: number;
  };
}

export const Route = createFileRoute("/_authenticated/account/user/")({
  component: UserMenuPage,
});

/**
 * Personal account menu page (workspace root for the user). 1:1 port of
 * legacy `account/user/page.tsx` (PersonalSectionPage).
 */
function UserMenuPage() {
  const { t } = useLingui();

  useDesktopRedirect("/account/user/dashboard");

  const referral = useReferralProgram();

  const { data, isLoading } = useQuery({
    queryKey: ["api", "/api/account/sidebar-data"],
    queryFn: () => apiFetch.get<SidebarData>("/api/account/sidebar-data"),
  });

  const profile = data?.profile;
  const notifCount = data?.notificationCount ?? 0;
  const unreadMessages = data?.unreadMessageCount ?? 0;
  const hasPin = data ? !!(data.vaultProtection && data.vaultProtection !== "none") : true;

  // showWallet: country has referral feature OR user already has balance.
  const showWallet = data?.countryReferralEnabled !== false || referral.balance > 0;

  const items: MenuItem[] = [
    {
      href: "/account/user/dashboard",
      label: <Trans>Summary</Trans>,
      icon: <LayoutDashboard className="size-5" />,
    },
    {
      href: "/account/user/appointments",
      label: <Trans>Appointments</Trans>,
      icon: <Calendar className="size-5" />,
      badge: profile?.appointmentCount,
    },
    {
      href: "/account/user/collections",
      label: <Trans>Saved collections</Trans>,
      icon: <Bookmark className="size-5" />,
      badges: [
        { icon: <Heart className="size-3" />, count: profile?.favoriteCount ?? 0 },
        { icon: <Search className="size-3" />, count: profile?.savedSearchCount ?? 0 },
      ],
    },
    {
      href: "/account/user/family",
      label: <Trans>Family</Trans>,
      icon: <Users className="size-5" />,
    },
    {
      href: "/account/user/medical-vault",
      label: <Trans>Medical vault</Trans>,
      icon: <Shield className="size-5" />,
      trailing: (
        <Link
          to="/account/user/settings"
          search={{ tab: "security", sub: "personal" } as never}
          className="text-muted-foreground hover:text-foreground inline-flex size-11 shrink-0 items-center justify-center transition-colors"
          aria-label={hasPin ? t`Vault protected` : t`Vault not protected`}
        >
          <ProtectionLockIcon isProtected={hasPin} className="size-4" />
        </Link>
      ),
    },
    ...(showWallet
      ? [
          {
            href: "/account/user/wallet",
            label: <Trans>Wallet</Trans>,
            icon: <Wallet className="size-5" />,
          } satisfies MenuItem,
        ]
      : []),
    {
      href: "/account/user/messages",
      label: <Trans>Messages</Trans>,
      icon: <MessageSquare className="size-5" />,
      badge: unreadMessages > 0 ? unreadMessages : undefined,
      badgeVariant: unreadMessages > 0 ? ("destructive" as const) : ("default" as const),
    },
    {
      href: "/account/user/notifications",
      label: <Trans>Notifications</Trans>,
      icon: <Bell className="size-5" />,
      badge: notifCount > 0 ? notifCount : undefined,
      badgeVariant: notifCount > 0 ? ("destructive" as const) : ("default" as const),
    },
    {
      href: "/account/user/billing",
      label: <Trans>Subscriptions & Invoices</Trans>,
      icon: <CreditCard className="size-5" />,
    },
    {
      href: "/account/user/settings",
      label: <Trans>Settings</Trans>,
      icon: <Settings className="size-5" />,
    },
  ];

  return (
    <>
      {/* The visible breadcrumb is hidden below sm, so the page still needs
          its own heading for screen readers (P27). */}
      <h1 className="sr-only">
        <Trans>Personal account</Trans>
      </h1>
      <MenuBreadcrumb
        homeHref="/"
        homeLabel={<Trans>Home</Trans>}
        currentLabel={<Trans>Personal account</Trans>}
      />
      <div className="-m-4 pt-4 md:-m-6 md:pt-6">
        {isLoading ? (
          <div className="space-y-2 px-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <>
            <SectionLabel>
              <Trans>Account</Trans>
            </SectionLabel>
            <MenuItemList items={items} />

            <div className="mx-4 mt-4">
              <UpgradeIncentiveCard
                workspace="personal"
                planTier={data?.subscriptionPlan === "premium" ? "premium" : "free"}
                billingHref="/account/user/billing"
                variant="card"
              />
            </div>

            <WorkspaceFooterSections />
          </>
        )}
      </div>
    </>
  );
}
